import React, { useState, useEffect } from 'react';
import { X, ArrowLeftRight, Package, Loader2, DollarSign, MessageSquare, ChevronRight } from 'lucide-react';
import axios from '../lib/axios';
import { useAuth, API } from '../App';
import { toast } from '../utils/toastWithSound';

const CounterOfferModal = ({ isOpen, onClose, proposal, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Select type, 2: Details, 3: Review
  const [offerType, setOfferType] = useState(null); // 'listing', 'items', 'mixed'
  const [myListings, setMyListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [itemsDescription, setItemsDescription] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      fetchMyListings();
    }
  }, [isOpen, user]);

  // Body scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setStep(1);
      setOfferType(null);
      setSelectedListing(null);
      setItemsDescription('');
      setEstimatedValue('');
      setMessage('');
    }
  }, [isOpen]);

  const fetchMyListings = async () => {
    setLoadingListings(true);
    try {
      const response = await axios.get(`${API}/listings/user/${user.id}`);
      // Filter out the listing being traded for
      const listings = (response.data.listings || response.data || [])
        .filter(l => l.id !== proposal?.listing_id && l.status === 'active');
      setMyListings(listings);
    } catch (error) {
      console.error('Failed to fetch listings:', error);
    } finally {
      setLoadingListings(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const counterData = {
        counter_listing_id: selectedListing?.id || null,
        counter_items_description: itemsDescription || null,
        counter_value: estimatedValue ? parseFloat(estimatedValue) : null,
        message: message || null
      };

      await axios.post(
        `${API}/trade-proposals/${proposal.id}/counter`,
        counterData);

      toast.success('Counter offer sent!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to send counter offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to send counter offer');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return offerType !== null;
    if (step === 2) {
      if (offerType === 'listing') return selectedListing !== null;
      if (offerType === 'items') return itemsDescription.trim().length > 0;
      if (offerType === 'mixed') return selectedListing !== null || itemsDescription.trim().length > 0;
    }
    return true;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return 'N/A';
    return `$${parseFloat(price).toLocaleString()}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="counter-offer-modal">
      <div className="bg-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl border border-border">
        {/* Header */}
        <div className="p-4 border-b border-border bg-gradient-to-r from-amber-500/10 to-orange-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <ArrowLeftRight className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-foreground">Counter Offer</h2>
                <p className="text-xs text-muted-foreground">For: {proposal?.listing_title}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
          
          {/* Progress steps */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? 'bg-amber-500' : 'bg-secondary'}`} />
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Select Offer Type */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">What would you like to offer instead?</p>
              
              <button
                onClick={() => setOfferType('listing')}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  offerType === 'listing' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-border hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">One of My Listings</p>
                    <p className="text-xs text-muted-foreground">Offer a different item you have listed</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setOfferType('items')}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  offerType === 'items' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-border hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Describe Items</p>
                    <p className="text-xs text-muted-foreground">Describe what you want instead</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setOfferType('mixed')}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  offerType === 'mixed' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-border hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Mixed Offer</p>
                    <p className="text-xs text-muted-foreground">Listing + additional items or cash</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Listing selector for 'listing' or 'mixed' */}
              {(offerType === 'listing' || offerType === 'mixed') && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {offerType === 'listing' ? 'Select a listing to offer' : 'Select a listing (optional)'}
                  </label>
                  {loadingListings ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                    </div>
                  ) : myListings.length === 0 ? (
                    <div className="text-center py-6 text-muted-foreground bg-secondary/50 rounded-xl">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No active listings available</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {myListings.map(listing => (
                        <button
                          key={listing.id}
                          onClick={() => setSelectedListing(selectedListing?.id === listing.id ? null : listing)}
                          className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                            selectedListing?.id === listing.id 
                              ? 'border-amber-500 bg-amber-500/10' 
                              : 'border-border hover:border-amber-500/50'
                          }`}
                        >
                          <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                            {listing.images?.[0] ? (
                              <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">{listing.title}</p>
                            <p className="text-sm text-amber-600 dark:text-amber-400">{formatPrice(listing.price)}</p>
                          </div>
                          {selectedListing?.id === listing.id && (
                            <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                              <ChevronRight className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Items description for 'items' or 'mixed' */}
              {(offerType === 'items' || offerType === 'mixed') && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {offerType === 'items' ? 'Describe what you want' : 'Additional items or requirements (optional)'}
                  </label>
                  <textarea
                    value={itemsDescription}
                    onChange={(e) => setItemsDescription(e.target.value)}
                    placeholder="E.g., Looking for a newer model, or add $50 cash, or include accessories..."
                    className="w-full px-4 py-3 bg-secondary rounded-xl border border-border focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none text-foreground placeholder:text-muted-foreground"
                    rows={3}
                  />
                </div>
              )}

              {/* Estimated value */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Estimated Value (optional)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <input
                    type="number"
                    value={estimatedValue}
                    onChange={(e) => setEstimatedValue(e.target.value)}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-3 bg-secondary rounded-xl border border-border focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Add a note to explain your counter offer..."
                  className="w-full px-4 py-3 bg-secondary rounded-xl border border-border focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none text-foreground placeholder:text-muted-foreground"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/30">
                <h3 className="font-semibold text-foreground mb-3">Your Counter Offer</h3>
                
                {selectedListing && (
                  <div className="flex items-center gap-3 p-3 bg-card rounded-lg mb-2">
                    <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                      {selectedListing.images?.[0] ? (
                        <img src={selectedListing.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Package className="w-full h-full p-3 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{selectedListing.title}</p>
                      <p className="text-sm text-amber-600">{formatPrice(selectedListing.price)}</p>
                    </div>
                  </div>
                )}

                {itemsDescription && (
                  <div className="p-3 bg-card rounded-lg mb-2">
                    <p className="text-xs text-muted-foreground mb-1">Additional requirements:</p>
                    <p className="text-sm text-foreground">{itemsDescription}</p>
                  </div>
                )}

                {estimatedValue && (
                  <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                    Estimated value: ${parseFloat(estimatedValue).toLocaleString()}
                  </p>
                )}

                {message && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Your message:</p>
                    <p className="text-sm text-foreground italic">"{message}"</p>
                  </div>
                )}
              </div>

              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-xs text-muted-foreground">
                  Sending this counter offer will notify {proposal?.proposer_name || 'the buyer'} and they can accept, decline, or continue negotiating.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-secondary/30">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2.5 text-foreground font-medium hover:bg-secondary rounded-lg transition-colors"
              >
                Back
              </button>
            )}
            <div className="flex-1" />
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={!canProceed()}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <ArrowLeftRight className="w-4 h-4" />
                    Send Counter Offer
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounterOfferModal;
